export function normalizeWiringValue(field, raw) {
  if (field?.type === 'checkbox') return Boolean(raw);
  return raw ?? '';
}

export function computeVersionState({ local, remote, transfer }) {
  const tr = transfer || null;

  if (tr?.status === 'downloading') return 'download_in_progress';
  if (tr?.status === 'completed') return 'download_finished';
  if (tr?.status === 'failed') return 'download_not_finished';
  if (tr?.status !== 'completed' && tr?.status_desc) return 'download_not_finished';

  const localId = String(local?.created_at || '').trim();
  const remoteId = String(remote?.created_at || '').trim();

  if (!localId && !remoteId) return 'no_version';
  if (remoteId && !localId) return 'available_version';
  if (localId && remoteId && localId !== remoteId) return 'available_version';

  return 'up_to_date';
}

export function computeProgressPct(transfer) {
  const tr = transfer || null;
  if (!tr) return 0;

  const total = Number(tr.total_size_bytes ?? 0);
  const downloaded = Number(tr.downloaded_bytes ?? 0);
  if (!total) return 0;

  const pct = Math.floor((downloaded / total) * 100);
  return Math.max(0, Math.min(100, pct));
}

export function versionLabel(img) {
  if (!img) return '-';
  const v = img?.version_tag || img?.version || img?.tag || '-';
  return String(v || '-');
}

export function displayedVersionFor(state, local, remote) {
  if (state === 'available_version') return versionLabel(remote);
  if (state === 'download_in_progress') return versionLabel(remote);
  if (state === 'download_finished') return versionLabel(remote);
  if (state === 'download_not_finished') return versionLabel(remote);
  return versionLabel(local) !== '-' ? versionLabel(local) : versionLabel(remote);
}

export function formatRemainingTimeMinutes(t, remainingSec) {
  if (typeof remainingSec !== 'number') return '-';
  const m = Math.max(1, Math.ceil(remainingSec / 60));
  return t('sidebar_status.minutes', { count: m });
}

export function estimateDownloadMinutes(sizeBytes, downlinkBps) {
  const total = Number(sizeBytes || 0);
  const downBps = Number(downlinkBps || 0);

  if (!total || !downBps) return null;

  const sec = Math.ceil((total * 8) / downBps);
  return Math.max(1, Math.ceil(sec / 60));
}

export function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  if (h > 0) return `${h}h ${m}m ${ss}s`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}

export function formatBps(bps) {
  const v = Number(bps) || 0;
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Gbps`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} Mbps`;
  if (v >= 1_000) return `${Math.floor(v / 1_000)} Kbps`;
  return `${Math.floor(v)} bps`;
}

export function formatBytes(bytes) {
  const v = Number(bytes) || 0;
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)} GB`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} MB`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(2)} KB`;
  return `${Math.floor(v)} B`;
}

export function formatAge(ageSeconds) {
  const s = Math.max(0, Math.floor(Number(ageSeconds) || 0));
  if (s < 10) return 'just now';

  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);

  if (h > 0) {
    const mm = m % 60;
    return `${h}h ${mm}m ago`;
  }

  if (m > 0) {
    const ss = s % 60;
    return `${m}m ${ss}s ago`;
  }

  return `${s}s ago`;
}
